/**
 * ProspectLinksSection Component
 *
 * The web links on a prospect's profile: a PBR or Perfect Game page, a highlight reel, a school
 * roster page. A plain list by design; there is no label or link type.
 *
 * Baseball only. The API refuses to add a link for a softball program, and this section is not
 * rendered at all for one. Links are hidden rather than deleted, so they come back if the program
 * switches back to baseball.
 */

import { useEffect, useId, useState, type FormEvent } from "react";
import { Button } from "../../../../../components/Button";
import { ConfirmModal } from "../../../../../components/ConfirmModal";
import { TextInput } from "../../../../../components/TextInput";
import type { RosterSport } from "../../../../../constants/sport";
import {
  createProspectLink,
  deleteProspectLink,
  getProspectLinks,
} from "../../../../../services/prospect-links";
import { getErrorMessage } from "../../../../../utils/errorHandler";
import { formatLinkForDisplay, isWebUrl } from "../../../../../utils/profileLinkDisplay";
import type { ProspectLink } from "../../../../../types/prospect-links";
import "./ProspectLinksSection.css";

interface ProspectLinksSectionProps {
  prospectId: string;
  /** The organization's sport, already normalised through `toRosterSport`. */
  sport: RosterSport;
}

export function ProspectLinksSection({ prospectId, sport }: ProspectLinksSectionProps) {
  if (sport !== "baseball") return null;
  // Keyed on the prospect so switching players in the viewer panel starts from a clean slate:
  // no half-typed link or open confirm carried over onto someone else's profile.
  return <ProspectLinksList key={prospectId} prospectId={prospectId} />;
}

function ProspectLinksList({ prospectId }: { prospectId: string }) {
  const titleId = useId();

  const [links, setLinks] = useState<ProspectLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [draft, setDraft] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const [pendingRemove, setPendingRemove] = useState<ProspectLink | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getProspectLinks(prospectId)
      .then((next) => {
        if (!cancelled) setLinks(next);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(getErrorMessage(err, "Could not load links."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [prospectId]);

  const handleAdd = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (adding) return;
    if (!draft.trim()) {
      setAddError("Enter a link to add.");
      return;
    }

    setAdding(true);
    setAddError(null);
    try {
      const created = await createProspectLink(prospectId, { url: draft });
      setLinks((prev) => [...prev, created]);
      setDraft("");
    } catch (err) {
      // The API's messages are written for coaches (bad address, duplicate, the 100 cap).
      setAddError(getErrorMessage(err, "Could not add the link. Try again."));
    } finally {
      setAdding(false);
    }
  };

  const handleConfirmRemove = async () => {
    if (!pendingRemove || removing) return;
    const target = pendingRemove;
    setRemoving(true);
    setRemoveError(null);
    try {
      await deleteProspectLink(prospectId, target.id);
      setLinks((prev) => prev.filter((l) => l.id !== target.id));
    } catch (err) {
      setRemoveError(getErrorMessage(err, "Could not remove the link. Try again."));
    } finally {
      setRemoving(false);
      setPendingRemove(null);
    }
  };

  return (
    <section
      className="prospect-links prospect-viewer-panel__section"
      aria-labelledby={titleId}
    >
      <h4 id={titleId} className="prospect-links__title">
        Links
      </h4>

      {loadError && (
        <div className="prospect-links__error" role="alert">
          {loadError}
        </div>
      )}
      {removeError && (
        <div className="prospect-links__error" role="alert">
          {removeError}
        </div>
      )}

      {loading ? (
        <p className="prospect-links__status">Loading links...</p>
      ) : links.length === 0 ? (
        !loadError && <p className="prospect-links__status">No links yet</p>
      ) : (
        <ul className="prospect-links__list">
          {links.map((link) => {
            const label = formatLinkForDisplay(link.url);
            return (
              <li key={link.id} className="prospect-links__item">
                {isWebUrl(link.url) ? (
                  <a
                    className="prospect-links__link"
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={link.url}
                  >
                    {label}
                  </a>
                ) : (
                  <span className="prospect-links__link">{label}</span>
                )}
                <Button
                  type="button"
                  variant="secondary"
                  className="prospect-links__remove"
                  onClick={() => {
                    setRemoveError(null);
                    setPendingRemove(link);
                  }}
                  aria-label={`Remove link ${label}`}
                >
                  Remove
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      <form className="prospect-links__add" onSubmit={handleAdd} noValidate>
        <TextInput
          className="prospect-links__input"
          label="Add link"
          placeholder="pbr.com/player/123"
          type="text"
          inputMode="url"
          autoComplete="off"
          spellCheck={false}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (addError) setAddError(null);
          }}
          error={addError ?? undefined}
          disabled={adding}
        />
        <Button type="submit" variant="primary" loading={adding} disabled={adding}>
          Add link
        </Button>
      </form>

      <ConfirmModal
        isOpen={pendingRemove !== null}
        onClose={() => {
          if (!removing) setPendingRemove(null);
        }}
        onConfirm={handleConfirmRemove}
        title="Remove link"
        message={
          pendingRemove
            ? `Remove ${formatLinkForDisplay(pendingRemove.url)} from this player?`
            : ""
        }
        confirmText={removing ? "Removing..." : "Remove"}
        cancelText="Cancel"
        busy={removing}
      />
    </section>
  );
}
